import type { ReactNode } from 'react';
import { clsx } from 'clsx';
import patterns from '@/shared/ui/styles/patterns.module.css';
import type { Source, VerificationStatus } from '../api/verificationApi';
import { sourceDisplay, statusDisplay, tierDisplay } from '../utils/format';
import styles from './chips.module.css';

export function StatusPill({
  status,
  failureCode,
}: {
  status: VerificationStatus;
  failureCode?: string | null;
}) {
  const display = statusDisplay(status, failureCode);
  return (
    <span className={clsx(patterns[display.pill], styles.statusPill)}>
      <span className={styles.dot} aria-hidden />
      {display.label}
    </span>
  );
}

export function SourceChip({ source }: { source: Source | string | null | undefined }) {
  const display = sourceDisplay(source);
  if (!display) return <span className={patterns.cellMuted}>—</span>;
  return <span className={clsx(patterns[display.pill], styles.chip)}>{display.label}</span>;
}

export function SourceChips({ sources }: { sources: Source[] }) {
  if (sources.length === 0) return null;
  const unique = Array.from(new Set(sources));
  return (
    <div className={styles.chipRow}>
      {unique.map((s) => (
        <SourceChip key={s} source={s} />
      ))}
    </div>
  );
}

export function TierChip({ tier }: { tier: string | null | undefined }) {
  const display = tierDisplay(tier);
  if (!display) return null;
  return <span className={clsx(patterns[display.pill], styles.chip)}>{display.label}</span>;
}

/** Amber "Needs call" / grey "not returned" marker for empty values. */
export function NeedsMarker({
  tone = 'warning',
  children,
}: {
  tone?: 'warning' | 'muted';
  children: ReactNode;
}) {
  return (
    <span
      className={clsx(styles.marker, tone === 'warning' ? styles.markerWarning : styles.markerMuted)}
    >
      {tone === 'warning' && <span className={styles.dot} aria-hidden />}
      {children}
    </span>
  );
}
